// @ts-strict-ignore
import { PropertyVO } from '@project/components/article/model/vo/property.vo';
import { Connection } from '@3d-scene/model3d/connection';
import { JsonObject, JsonProperty } from '../convert/convert-core';

@JsonObject('MetaElementVO')
export class MetaElementVO {
  public static readonly CLASS_NAME: string = 'MetaElementVO';

  public static readonly CONNECTION_PREFIX: string = 'cn';
  public static readonly TEXTURE_PREFIX: string = 'ta';

  @JsonProperty('type', 'String')
  public type: string = undefined;

  @JsonProperty('name', 'String')
  public name: string = undefined;

  @JsonProperty('corridor', 'Number')
  public corridor: number = undefined;

  @JsonProperty('orderNumber', 'Number')
  public orderNumber: number = undefined;

  @JsonProperty('behaviorProperties', '[PropertyVO]')
  public behaviorProperties: PropertyVO[] = undefined;

  @JsonProperty('isInternal', 'Boolean')
  public isInternal: boolean = undefined;

  public constructor() {
    this.type = MetaElementVO.CLASS_NAME;
  }
}

@JsonObject('ConnectionFreedomVO')
export class ConnectionFreedomVO {
  /**
	 * 0 if there is freedom along u axis, 1 otherwise
	 */
  @JsonProperty('uFreedom', 'Number')
  public uFreedom: number = undefined;

  /**
	 * 0 if there is freedom along v axis, 1 otherwise
	 */
  @JsonProperty('vFreedom', 'Number')
  public vFreedom: number = undefined;

  /**
	 * 0 if there is freedom along n axis, 1 otherwise
	 */
  @JsonProperty('nFreedom', 'Number')
  public nFreedom: number = undefined;
}

@JsonObject('ConnectionAreaVO')
export class ConnectionAreaVO extends MetaElementVO {
  public static readonly CLASS_NAME: string = 'ConnectionAreaVO';

  public static readonly SIDE_ACCEPTING: string = 'ACCEPTING';
  public static readonly SIDE_GIVING: string = 'GIVING';

  @JsonProperty('connectionSide', 'String')
  public connectionSide: string = undefined;

  @JsonProperty('connectionFreedom', 'ConnectionFreedomVO')
  public connectionFreedom: ConnectionFreedomVO = undefined;

  @JsonProperty('connectionRequirements', '[PropertyVO]')
  public connectionRequirements: PropertyVO[] = undefined;

  @JsonProperty('connectionFeatures', '[PropertyVO]')
  public connectionFeatures: PropertyVO[] = undefined;

  @JsonProperty('connectionPlausiProperties', '[PropertyVO]')
  public connectionPlausiProperties: PropertyVO[] = undefined;

  @JsonProperty('connectionMultiplicity', 'Number')
  public connectionMultiplicity: number = undefined;

  @JsonProperty('bothsides', 'Boolean')
  public bothsides: boolean = undefined;

  @JsonProperty('connectionConstructionProperties', '[PropertyVO]')
  public connectionConstructionProperties: PropertyVO[] = undefined;

  public constructor() {
    super();
    this.type = ConnectionAreaVO.CLASS_NAME;
  }
}

export class InteractionAreaVO extends MetaElementVO {
  public static readonly CLASS_NAME: string = 'InteractionAreaVO';

  public constructor() {
    super();
    this.type = InteractionAreaVO.CLASS_NAME;
  }
}

export class LightingAreaVO extends MetaElementVO {
  public static readonly CLASS_NAME: string = 'LightingAreaVO';

  public constructor() {
    super();
    this.type = LightingAreaVO.CLASS_NAME;
  }
}

@JsonObject('MontagePointAreaVO')
export class MontagePointAreaVO extends MetaElementVO {
  public static readonly CLASS_NAME: string = 'MontagePointAreaVO';

  public static readonly MONTAGE: string = 'MONTAGE';

  @JsonProperty('montageType', 'String')
  public montageType: string = undefined;

  public constructor() {
    super();
    this.type = MontagePointAreaVO.CLASS_NAME;
  }
}

export class ReflectionAreaVO extends MetaElementVO {
  public static readonly CLASS_NAME: string = 'ReflectionAreaVO';

  public constructor() {
    super();
    this.type = ReflectionAreaVO.CLASS_NAME;
  }
}

@JsonObject('RGBColorValueVO')
export class RGBColorValueVO {
  @JsonProperty('r', 'Number')
  public r: number = 0;

  @JsonProperty('g', 'Number')
  public g: number = 0;

  @JsonProperty('b', 'Number')
  public b: number = 0;

  constructor(r: number = 0, g: number = 0, b: number = 0) {
    this.r = r;
    this.g = g;
    this.b = b;
  }

  public toString(): string {
    return 'rgb(' + this.r + ', ' + this.g + ', ' + this.b + ')';
  }

  public equals(color: RGBColorValueVO): boolean {
    if (!color) {
      return false;
    }
    return this.r === color.r && this.g === color.g && this.b === color.b;
  }
}

@JsonObject('TextValueVO')
export class TextValueVO {
  @JsonProperty('type', 'String')
  public type: string = undefined;

  public equals(value: TextValueVO): boolean {
    return (!value) ? false : true;
  }

  public clone(): TextValueVO {
    return new TextValueVO();
  }
}

@JsonObject('SimpleTextValueVO')
export class SimpleTextValueVO extends TextValueVO {

  public static readonly CLASS_NAME: string = 'SimpleTextValueVO';

  @JsonProperty('text', 'String')
  public text: string = null;

  /**
   * rotation angle of the text. The value is in the range of [0,359]
   */
  @JsonProperty('rotation', 'Number')
  public rotation: number = 0;

  /**
   * horizontal alignment of the text within its area. The possible values are:
	 * -1 - left
	 * 0 - center
	 * 1 - right
   */
  @JsonProperty('hAlignment', 'Number')
  public hAlignment: number = 0;

  /**
   * vertical alignment of the text within its area. The possible values are:
	 * -1 - top
	 * 0 - center
	 * 1 - bottom
   */
  @JsonProperty('vAlignment', 'Number')
  public vAlignment: number = 0;

  /**
   * background color of the text
   */
  @JsonProperty('bgColor', 'RGBColorValueVO')
  public bgColor: RGBColorValueVO = new RGBColorValueVO();

  constructor(args: Partial<SimpleTextValueVO> = {}) {
    super();
    this.type = SimpleTextValueVO.CLASS_NAME;
    Object.assign(this, args);
  }

  public equals(simpleValue: TextValueVO): boolean {
    if (!(simpleValue instanceof SimpleTextValueVO)) {
      return false;
    }
    if (!this.bgColor) {
      if (simpleValue.bgColor) {
        return false;
      }
    } else {
      if (!this.bgColor.equals(simpleValue.bgColor)) {
        return false;
      }
    }
    return this.text === simpleValue.text && this.rotation === simpleValue.rotation
      && this.hAlignment === simpleValue.hAlignment && this.vAlignment === simpleValue.vAlignment
      && super.equals(simpleValue);
  }

  public clone(): TextValueVO {
    const result: SimpleTextValueVO = new SimpleTextValueVO();
    this.fillClonedObject(result);
    return result;
  }

  protected fillClonedObject(clonedObject: SimpleTextValueVO): void {
    if (!clonedObject) {
      return;
    }
    clonedObject.text = this.text;
    clonedObject.rotation = this.rotation;
    clonedObject.hAlignment = this.hAlignment;
    clonedObject.vAlignment = this.vAlignment;
    clonedObject.bgColor = new RGBColorValueVO();
    clonedObject.bgColor.b = this.bgColor.b;
    clonedObject.bgColor.g = this.bgColor.g;
    clonedObject.bgColor.r = this.bgColor.r;
  }
}

@JsonObject('CompressionSimpleTextValueVO')
export class CompressionSimpleTextValueVO extends SimpleTextValueVO {

  public static readonly CLASS_NAME: string = 'CompressionSimpleTextValueVO';

  @JsonProperty('compression', 'Number')
  public compression: number = 100;

  @JsonProperty('autoCompression', 'Boolean')
  public autoCompression: boolean = true;

  @JsonProperty('autoFontSize', 'Boolean')
  public autoFontSize: boolean = true;

  public constructor(args: Partial<CompressionSimpleTextValueVO> = {}) {
    super();
    this.type = CompressionSimpleTextValueVO.CLASS_NAME;
    Object.assign(this, args);
  }

  public equals(value: TextValueVO): boolean {
    if (!(value instanceof CompressionSimpleTextValueVO)) {
      return false;
    }
    return (super.equals(value) && this.compression === value.compression
      && this.autoCompression === value.autoCompression && this.autoFontSize === value.autoFontSize);
  }

  public clone(): TextValueVO {
    const result: CompressionSimpleTextValueVO = new CompressionSimpleTextValueVO();
    this.fillClonedObject(result);
    result.compression = this.compression;
    result.autoCompression = this.autoCompression;
    result.autoFontSize = this.autoFontSize;
    return result;
  }
}

@JsonObject('ImageValueVO')
export class ImageValueVO {
  @JsonProperty('url', 'String')
  public url: string = undefined;
}

@JsonObject('TextureAreaVO')
export class TextureAreaVO extends MetaElementVO {
  public static readonly CLASS_NAME: string = 'TextureAreaVO';

  public static readonly TYPE_TEXT: string = 'TEXT';
  public static readonly TYPE_IMAGE: string = 'IMAGE';
  public static readonly TYPE_TEXT_OR_IMAGE_USAGE: string = 'TEXT_OR_IMAGE_USAGE';
  public static readonly TYPE_COLOR: string = 'COLOR';

  public static readonly TEXT_TYPES: string[] = [TextureAreaVO.TYPE_TEXT, TextureAreaVO.TYPE_TEXT_OR_IMAGE_USAGE];
  public static readonly IMAGE_USAGE_TYPES: string[] = [TextureAreaVO.TYPE_TEXT_OR_IMAGE_USAGE];

  @JsonProperty('textureType', 'String')
  public textureType: string = undefined;

  @JsonProperty('defaultValueAsText', 'SimpleTextValueVO')
  public defaultValueAsText: SimpleTextValueVO = undefined;

  @JsonProperty('defaultValueAsImage', 'ImageValueVO')
  public defaultValueAsImage: ImageValueVO = undefined;

  @JsonProperty('defaultValueAsColor', 'RGBColorValueVO')
  public defaultValueAsColor: RGBColorValueVO = undefined;

  public constructor() {
    super();
    this.type = TextureAreaVO.CLASS_NAME;
  }
}

@JsonObject('MetaElementUsageIdentityVO')
export class MetaElementUsageIdentityVO {
  @JsonProperty('articleUsageId', 'String')
  public articleUsageId: string = undefined;

  @JsonProperty('metaElementName', 'String')
  public metaElementName: string = undefined;

  public static create(articleUsageId: string, metaElementName: string): MetaElementUsageIdentityVO {
    const metaElementUsageIdentityVO: MetaElementUsageIdentityVO = new MetaElementUsageIdentityVO();
    metaElementUsageIdentityVO.articleUsageId = articleUsageId;
    metaElementUsageIdentityVO.metaElementName = metaElementName;
    return metaElementUsageIdentityVO;
  }

  public equals(other: MetaElementUsageIdentityVO | undefined): boolean {
    if (!other) {
      return false;
    }
    return other.articleUsageId === this.articleUsageId && other.metaElementName === this.metaElementName;
  }

  public toString(): string {
    return '' + this.articleUsageId + ':' + this.metaElementName;
  }

  public clone(): MetaElementUsageIdentityVO {
    const clone: MetaElementUsageIdentityVO = new MetaElementUsageIdentityVO();
    clone.articleUsageId = this.articleUsageId;
    clone.metaElementName = this.metaElementName;
    return clone;
  }
}

@JsonObject('MetaElementUsageVO')
export class MetaElementUsageVO {
  public static readonly CLASS_NAME: string = 'MetaElementUsageVO';

  @JsonProperty('type', 'String')
  public type: string = undefined;

  @JsonProperty('identity', 'MetaElementUsageIdentityVO')
  public identity: MetaElementUsageIdentityVO = undefined;

  public constructor() {
    this.type = MetaElementUsageVO.CLASS_NAME;
  }
}

@JsonObject('ConnectionVO')
export class ConnectionVO {
  @JsonProperty('acceptingAreaId', 'MetaElementUsageIdentityVO')
  public acceptingAreaId: MetaElementUsageIdentityVO = undefined;
  @JsonProperty('givingAreaIds', '[MetaElementUsageIdentityVO]')
  public givingAreaIds: MetaElementUsageIdentityVO[] = undefined;
}

@JsonObject('ConnectionAreaUsageVO')
export class ConnectionAreaUsageVO extends MetaElementUsageVO {
  public static readonly CLASS_NAME: string = 'ConnectionAreaUsageVO';

  @JsonProperty('connection', 'ConnectionVO')
  public connection: ConnectionVO = undefined;

  public constructor() {
    super();
    this.type = ConnectionAreaUsageVO.CLASS_NAME;
  }
}

@JsonObject('TableTextItemVO')
export class TableTextItemVO {
  @JsonProperty('value', 'TextValueVOConverter', true)
  public value: TextValueVO = undefined;
  @JsonProperty('rowSpan', 'Number')
  public rowSpan: number = 1;
  @JsonProperty('colSpan', 'Number')
  public colSpan: number = 1;

  public static create(rowSpan: number = 1, colSpan: number = 1): TableTextItemVO {
    const item: TableTextItemVO = new TableTextItemVO();
    item.rowSpan = rowSpan;
    item.colSpan = colSpan;
    return item;
  }

  public equals(item: TableTextItemVO): boolean {
    if (!item) {
      return false;
    }
    if (!this.value) {
      if (item.value) {
        return false;
      }
    } else {
      if (!this.value.equals(item.value)) {
        return false;
      }
    }
    return this.rowSpan === item.rowSpan && this.colSpan === item.colSpan;
  }

  public clone(): TableTextItemVO {
    const result: TableTextItemVO = TableTextItemVO.create(this.rowSpan, this.colSpan);
    if (this.value) {
      result.value = this.value.clone() as SimpleTextValueVO;
    }
    return result;
  }
}

@JsonObject('TableTextRowVO')
export class TableTextRowVO {
  @JsonProperty('items', '[TableTextItemVO]')
  public items: TableTextItemVO[] = [];

  public equals(row: TableTextRowVO): boolean {
    if (!row) {
      return false;
    }
    if (this.items.length !== row.items.length) {
      return false;
    }
    for (let i: number = 0; i < this.items.length; i++) {
      if (!this.items[i].equals(row.items[i])) {
        return false;
      }
    }
    return true;
  }

  public clone(): TableTextRowVO {
    const result: TableTextRowVO = new TableTextRowVO();
    if (this.items) {
      result.items = [];
      for (const item of this.items) {
        result.items.push(item.clone());
      }
    }
    return result;
  }
}

@JsonObject('TableTextValueVO')
export class TableTextValueVO extends TextValueVO {

  public static readonly CLASS_NAME: string = 'TableTextValueVO';

  @JsonProperty('rows', '[TableTextRowVO]')
  public rows: TableTextRowVO[] = [];

  public constructor() {
    super();
    this.type = TableTextValueVO.CLASS_NAME;
  }

  public equals(tableValue: TextValueVO): boolean {
    if (!(tableValue instanceof TableTextValueVO)) {
      return false;
    }
    if (this.rows.length !== tableValue.rows.length) {
      return false;
    }
    for (let i: number = 0; i < this.rows.length; i++) {
      if (!this.rows[i].equals(tableValue.rows[i])) {
        return false;
      }
    }
    return super.equals(tableValue);
  }

  public clone(): TextValueVO {
    const result: TableTextValueVO = new TableTextValueVO();
    if (this.rows) {
      result.rows = [];
      for (const row of this.rows) {
        result.rows.push(row.clone());
      }
    }
    return result;
  }
}

@JsonObject('TextureAreaUsageVO')
export class TextureAreaUsageVO extends MetaElementUsageVO {
  public static readonly CLASS_NAME: string = 'TextureAreaUsageVO';

  @JsonProperty('imageUrl', 'String')
  public imageUrl: string = undefined;
  @JsonProperty('rColor', 'Number')
  public rColor: number = 0;
  @JsonProperty('gColor', 'Number')
  public gColor: number = 0;
  @JsonProperty('bColor', 'Number')
  public bColor: number = 0;
  @JsonProperty('textValue', 'TextValueVOConverter', true)
  public textValue: TextValueVO = undefined;
  @JsonProperty('isDirectPrint', 'Boolean')
  public isDirectPrint: boolean = false;

  // texture area usage ids of merged neighbor textures
  @JsonProperty('previousMergedUsageId', 'MetaElementUsageIdentityVO')
  public previousMergedUsageId: MetaElementUsageIdentityVO = undefined;
  @JsonProperty('nextMergedUsageId', 'MetaElementUsageIdentityVO')
  public nextMergedUsageId: MetaElementUsageIdentityVO = undefined;

  public constructor(args: Partial<TextureAreaUsageVO> = {}) {
    super();
    this.type = TextureAreaUsageVO.CLASS_NAME;
    Object.assign(this, args);
  }
}

@JsonObject('DefinedMetaElementPropertyVO')
export class DefinedMetaElementPropertyVO {
  @JsonProperty('identity', 'MetaElementUsageIdentityVO')
  public identity: MetaElementUsageIdentityVO = undefined;

  @JsonProperty('property', 'PropertyVOConverter', true)
  public property: PropertyVO = undefined;

  @JsonProperty('description', 'String')
  public description: string = undefined;
}

export class SelectionMetaElementUsageIdentity extends MetaElementUsageIdentityVO {
  public connectionUid: string;

  public constructor(articleUsageId: string, connection: Connection) {
    super();
    this.articleUsageId = articleUsageId;
    this.metaElementName = connection.id;
    this.connectionUid = connection.uid;
  }

  public equals(id: MetaElementUsageIdentityVO): boolean {
    if (id instanceof SelectionMetaElementUsageIdentity) {
      return super.equals(id) && id.connectionUid === this.connectionUid;
    } else {
      return super.equals(id);
    }
  }
}
